import React, { ReactElement, useState } from "react";

import {
  TextInput,
  LoginButton,
  GanttLogoContainer,
  Header,
  LoginOuter,
  LoginBoxInner,
  LoginFailure,
  LoginLanguageContainer,
  FailureMessage,
  AzureLoginLink,
} from "./LoginComponents";

import { useTranslation } from "react-i18next";
import LanguageSelector from "../common/LanguageSelector";
import { useMsal } from "@azure/msal-react";
import { AccountInfo } from "@azure/msal-browser";

interface ILoginScreenProps {
  tryLogin: (username: string, password: string, isWindowsUser: boolean, rememberMe: boolean) => Promise<string | null>;
  tryAzureLogin: (account: AccountInfo) => Promise<void>;
  setErrorMessage: (message: string) => void;
  errorMessage: string;
  isOauth2Enabled: boolean;
}

export const LoginScreen = (props: ILoginScreenProps): ReactElement => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isWindowsUser, setIsWindowsUser] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { t } = useTranslation();
  const { instance } = useMsal();

  const handleLogin = async () => {
    const errorMessage = await props.tryLogin(username, password, isWindowsUser, rememberMe);
    if (errorMessage) {
      // Can't simply call setErrorMessage(errorMessage) because if there were no errors, this component would have already been unmounted
      props.setErrorMessage(errorMessage);
    }
  };

  const handleEnter = (e) => {
    const KEYCODE_ENTER = 13;
    if (e.which === KEYCODE_ENTER && username && password) {
      handleLogin();
    }
  };

  const reset = () => {
    setUsername("");
    setPassword("");
    props.setErrorMessage(null);
  };

  const renderAzureLogin = () => {

    const elements = [];
    let key=0;

    for(const account of instance.getAllAccounts()) {
      elements.push(<AzureLoginLink key={key++} onClick={() => props.tryAzureLogin(account)}>{t("login.continueasuser", {name: account.username})}</AzureLoginLink>)
    }

    elements.push(<AzureLoginLink key={key} onClick={() => props.tryAzureLogin(null)}>{t("login.loginwithazure")}</AzureLoginLink>);

    return <div>{elements}</div>;
  }

  return (
    <>
      {!props.errorMessage && (
        <LoginOuter onKeyPress={handleEnter}>
          <LoginBoxInner>
            <GanttLogoContainer />
            <Header color="#6A9844">{t("login.header")}</Header>
            <LoginLanguageContainer>
              <LanguageSelector />
            </LoginLanguageContainer>
            <TextInput inputType={"text"} label={t("login.username")} value={username} onChange={setUsername} />
            <TextInput inputType={"password"} label={t("login.password")} value={password} onChange={setPassword} />
            <div>
              <input
                type="checkbox"
                checked={isWindowsUser}
                id="isWindowsUserCheckbox"
                onChange={(event) => setIsWindowsUser(event.target.checked)}
              />
              <label htmlFor="isWindowsUserCheckbox">{t("login.windowsuser").toString()}</label>
            </div>
            <div>
              <input
                type="checkbox"
                checked={rememberMe}
                id="rememberMeCheckbox"
                onChange={(event) => setRememberMe(event.target.checked)}
              />
              <label htmlFor="rememberMeCheckbox">{t("login.rememberme").toString()}</label>
            </div>
            {props.isOauth2Enabled && renderAzureLogin()}
            <br/>
            <LoginButton onClick={handleLogin}>{t("command.login")}</LoginButton>
          </LoginBoxInner>
        </LoginOuter>
      )}
      {props.errorMessage && (
        <LoginOuter>
          <LoginFailure>
            <Header color="#C65911">{t("login.errorheader")}</Header>
            <FailureMessage>{props.errorMessage}</FailureMessage>
            <LoginButton onClick={reset}>{t("command.back")}</LoginButton>
          </LoginFailure>
        </LoginOuter>
      )}
    </>
  );
};
